/**
 * A module's results, and its leaderboard.
 *
 * A module is played capsule by capsule, and every capsule play is already
 * recorded on its own: the player posts one to `app.bsky.feed.createPlay` with a
 * score and a time (see `#/state/queries/create-play`). Nothing new is measured
 * here. This module gathers those plays by their capsule and answers the two
 * questions a class asks about a playlist — *how did everyone do*, and *who won*.
 *
 * ## The ranking
 *
 * **Points per minute, over the whole playlist, finishers only.** Three decisions
 * are folded into that sentence and each of them is load-bearing:
 *
 *  - **Points *per minute*, not points, and not time.** A module is a run, and a
 *    run has two costs: how much you got right and how long you took to get it.
 *    Ranking on points alone rewards sitting there until the answers come;
 *    ranking on time alone rewards guessing. Dividing one by the other is what
 *    makes a fast, good run beat a slow, slightly better one — which is the
 *    behaviour the feature was asked for in as many words.
 *  - **Over the whole playlist.** The ratio is taken from the module's *totals*
 *    (every capsule's best play, summed), never from a single capsule's play, so
 *    the board measures the course rather than its best moment.
 *  - **Finishers only.** Without this the ratio is trivially gamed: play one
 *    capsule quickly, score ten out of ten, and post the highest points per
 *    minute in the class. Requiring the whole playlist is the entire defence, and
 *    it is also the honest reading of the gate — a leaderboard appears once a
 *    hundred learners have *finished*.
 *
 * ## The three other rules
 *
 *  - **Best attempt per capsule counts.** A learner who plays *Fractions II*
 *    three times keeps the best of the three (most points, then fastest): playing
 *    again to improve is allowed, and farming a capsule for points is not. Every
 *    other attempt still counts as a play, which is why `plays` is reported
 *    beside the scores.
 *  - **A play with no measured time gets no ratio.** `0` would rank it last and
 *    `Infinity` would rank it first; both are claims the data does not support.
 *    It is counted, it is shown, and it sorts where a zero ratio sorts — at the
 *    bottom of the finishers, which is where an unmeasured run belongs.
 *  - **The unfinished are listed, and never ranked.** They appear below the
 *    ranked rows with how far they got (`4 of 6`), because the alternative —
 *    ranking somebody on four capsules against people who did six — is a lie
 *    about the competition, and leaving them out is a lie about the class.
 */
import {MODULE_LEADERBOARD_MIN_FINISHERS} from '#/lib/modulePlaylist'

/** One capsule of the module, as the board needs it. */
export type ModuleLeaderboardCapsule = {
  uri: string
  title?: string
  /**
   * The most a learner can score on this capsule, when the caller knows it.
   *
   * Optional on purpose: the slides are what say it (see `#/lib/scoring`), and a
   * board that refused to draw itself until every capsule had been fetched would
   * trade a percentage for a spinner. Unknown max points cost the module its
   * percentage and nothing else.
   */
  maxPoints?: number
}

/**
 * One capsule play, as the AppView lists it.
 *
 * The same shape the book's class report reads (`#/lib/flashbookClassReport`
 * `ClassPlay`), because it is the same record: a learner's play of one piece of
 * work, with what they scored, how long it took and when it happened. What
 * differs is only the subject — there, one book across its pages; here, one
 * capsule within a playlist.
 */
export type ModulePlay = {
  capsuleUri: string
  learnerDid: string
  learnerHandle?: string
  learnerName?: string
  /**
   * The learner's profile photo, when the AppView sends one.
   *
   * Optional because it is the one field a board can be drawn without: a row with
   * no picture gets the app's own default, and a hundred rows resolved one profile
   * at a time would cost a hundred requests to put faces on a list.
   */
  learnerAvatar?: string
  /** Points earned, as the player counted them. */
  score?: number
  /** How long the play took, in ms. */
  timeMs?: number
  /** When it was played, used to order a learner's own attempts. */
  at?: string
  /**
   * The level the learner played at, when their play said so.
   *
   * Optional twice over: the field itself is an extension the AppView may not
   * implement, and a learner may have played before it existed. See
   * `ModulePlayerScore.level` for what a missing one means (nothing, not
   * "another level").
   */
  learnerLevel?: string
}

/** One learner's standing in the module. */
export type ModulePlayerScore = {
  did: string
  handle?: string
  name?: string
  /** The learner's profile photo, when anything said what it is. */
  avatar?: string
  /**
   * The school level this learner plays at, when a play of theirs said so.
   *
   * It travels with the row because one question needs it — *is this someone I
   * am competing with?* — and a row is where the answer already is (see
   * `#/lib/moduleAudience`). Most deployments do not send it, and a row without
   * one is not a row from another level; it is a row whose level nobody said.
   */
  level?: string
  /** Total points over the playlist: the best attempt at each capsule, summed. */
  points: number
  /**
   * The module's own maximum, when every capsule's is known.
   *
   * `null` means *unknown*, never zero — the same distinction the book's card
   * makes about a length it has not heard yet, and for the same reason: a
   * percentage of nothing is a statement about the request, not about the work.
   */
  maxPoints: number | null
  /** Total time over the playlist: the same best attempts. */
  timeMs: number
  /** Distinct capsules played. */
  capsulesDone: number
  /** Capsules in the module. */
  capsuleCount: number
  /** Whether they played every capsule — the only thing that earns a rank. */
  finished: boolean
  /** `points / (timeMs / 60000)`, or 0 when there is no measured time. */
  pointsPerMinute: number
  /** Every attempt at the module, best attempts included. */
  plays: number
  /** 1-based among the finishers; `null` for anyone who did not finish. */
  rank: number | null
}

export type ModuleLeaderboard = {
  /**
   * Which face the board wears.
   *
   * `results` below the threshold — everyone, ranked by the same ratio, with the
   * unfinished listed under them. `leaderboard` at or above it — the same ranking,
   * cut to a podium. One ranking, two presentations, chosen by one comparison.
   */
  mode: 'results' | 'leaderboard'
  /** Learners who played every capsule. */
  finishers: number
  /** How many finishers the top three wait for. */
  threshold: number
  /** How many plays the board was built from, across every learner. */
  plays: number
  capsuleCount: number
  /** The module's maximum, when every capsule's is known. */
  maxPoints: number | null
  /** The finishers, best first, each with its rank filled in. */
  ranked: ModulePlayerScore[]
  /** The 1st, 2nd and 3rd finishers — the podium, in that order. */
  podium: ModulePlayerScore[]
  /** Everyone who did not finish, closest to finishing first. */
  unfinished: ModulePlayerScore[]
  /** The whole class in one list, ranked first: what a results list draws. */
  all: ModulePlayerScore[]
  /** Nobody has played yet. */
  empty: boolean
}

/**
 * Points per minute, or 0 when there is no time to divide by.
 *
 * Exported because three callers need the *same* number under the same name: the
 * board's ranking, the row that prints it, and the module's own summary at the
 * end of a run. A second implementation of one division is how a learner's row
 * and their summary come to disagree in the fourth decimal place.
 */
export function pointsPerMinute(points: number, timeMs: number): number {
  if (!(timeMs > 0)) return 0
  return points / (timeMs / 60000)
}

/** One learner's attempts, keyed by the capsule they were at. */
type Attempts = {
  did: string
  handle?: string
  name?: string
  avatar?: string
  level?: string
  byCapsule: Map<string, {score: number; timeMs: number; at: string}>
  plays: number
}

/**
 * Whether one attempt is better than another.
 *
 * More points wins; equal points, the faster attempt wins; and if both are equal
 * the earlier one stays, so a learner's own two identical runs do not make the
 * board flicker between them on every refetch. The order is stated once, here,
 * because the alternative — "keep whichever the server happened to return
 * last" — is a scoreboard that changes when nothing changed.
 */
function isBetterAttempt(
  candidate: {score: number; timeMs: number; at: string},
  incumbent: {score: number; timeMs: number; at: string},
): boolean {
  if (candidate.score !== incumbent.score)
    return candidate.score > incumbent.score
  if (candidate.timeMs !== incumbent.timeMs)
    return candidate.timeMs < incumbent.timeMs
  return candidate.at < incumbent.at
}

/** A play's score, with everything the record did not say read as nothing. */
function scoreOf(play: ModulePlay): number {
  return typeof play.score === 'number' && Number.isFinite(play.score)
    ? Math.max(0, play.score)
    : 0
}

function timeOf(play: ModulePlay): number {
  return typeof play.timeMs === 'number' && Number.isFinite(play.timeMs)
    ? Math.max(0, play.timeMs)
    : 0
}

/**
 * The board.
 *
 * Pure, and deliberately total: every play is accepted, whatever it says — a row
 * with no learner, a score that is a string, a capsule the module no longer
 * holds. A class report is read by a teacher during a lesson (see the book's own
 * report for the full argument), and a board that threw on one malformed row
 * would take the whole lesson with it. What a play cannot say, it does not
 * contribute: a row with no learner is dropped, a row for a capsule outside the
 * module is ignored, a row with no score counts as zero points.
 */
export function buildModuleLeaderboard({
  capsules,
  plays,
  threshold = MODULE_LEADERBOARD_MIN_FINISHERS,
}: {
  capsules: readonly ModuleLeaderboardCapsule[]
  plays: readonly ModulePlay[] | undefined
  threshold?: number
}): ModuleLeaderboard {
  const capsuleCount = capsules.length
  const moduleUris = new Set(capsules.map(capsule => capsule.uri))
  const learners = new Map<string, Attempts>()
  let playCount = 0

  for (const play of plays ?? []) {
    const did =
      typeof play.learnerDid === 'string' ? play.learnerDid.trim() : ''
    if (!did) continue
    // A play of something the module does not hold: an old capsule that was
    // taken out, or the AppView answering about a uri this board never asked
    // for. Counting it would inflate a learner's points with work the module
    // does not contain.
    if (capsuleCount > 0 && !moduleUris.has(play.capsuleUri)) continue
    playCount++

    const attempts =
      learners.get(did) ??
      ({
        did,
        handle: play.learnerHandle,
        name: play.learnerName,
        avatar: play.learnerAvatar,
        level: play.learnerLevel,
        byCapsule: new Map(),
        plays: 0,
      } satisfies Attempts)
    if (!attempts.handle && play.learnerHandle)
      attempts.handle = play.learnerHandle
    if (!attempts.name && play.learnerName) attempts.name = play.learnerName
    if (!attempts.avatar && play.learnerAvatar)
      attempts.avatar = play.learnerAvatar
    if (!attempts.level && play.learnerLevel) attempts.level = play.learnerLevel
    attempts.plays++

    const candidate = {
      score: scoreOf(play),
      timeMs: timeOf(play),
      at: typeof play.at === 'string' ? play.at : '',
    }
    const incumbent = attempts.byCapsule.get(play.capsuleUri)
    if (!incumbent || isBetterAttempt(candidate, incumbent)) {
      attempts.byCapsule.set(play.capsuleUri, candidate)
    }
    learners.set(did, attempts)
  }

  const knownMax = capsules.every(
    capsule => typeof capsule.maxPoints === 'number',
  )
  const maxPoints = knownMax
    ? capsules.reduce((sum, capsule) => sum + (capsule.maxPoints ?? 0), 0)
    : null

  const scored: ModulePlayerScore[] = [...learners.values()].map(attempts => {
    let points = 0
    let timeMs = 0
    for (const best of attempts.byCapsule.values()) {
      points += best.score
      timeMs += best.timeMs
    }
    const capsulesDone = attempts.byCapsule.size
    /*
     * A module with no capsules cannot be finished by anybody.
     *
     * The guard is not pedantry: it is the difference between "nobody has played
     * this yet" and "everybody finished", and a board that said the second about
     * an empty playlist would put the whole class on a podium for playing nothing.
     */
    const finished = capsuleCount > 0 && capsulesDone >= capsuleCount
    return {
      did: attempts.did,
      handle: attempts.handle,
      name: attempts.name,
      avatar: attempts.avatar,
      level: attempts.level,
      points,
      maxPoints,
      timeMs,
      capsulesDone,
      capsuleCount,
      finished,
      pointsPerMinute: pointsPerMinute(points, timeMs),
      plays: attempts.plays,
      rank: null,
    }
  })

  /*
   * Two orders, because the two lists answer different questions.
   *
   * The ranked ask *who won*, and the answer is the ratio. The unfinished ask
   * *how far did they get*, and the answer is the count — the ratio of an
   * unfinished run is not comparable to a finished one and must not be used to
   * sort them, which is also why nobody in this list is given a rank.
   */
  const ranked = scored
    .filter(player => player.finished)
    .sort(
      (a, b) =>
        b.pointsPerMinute - a.pointsPerMinute ||
        a.timeMs - b.timeMs ||
        b.points - a.points ||
        (a.name ?? a.handle ?? a.did).localeCompare(
          b.name ?? b.handle ?? b.did,
        ),
    )
    .map((player, index) => ({...player, rank: index + 1}))

  const unfinished = scored
    .filter(player => !player.finished)
    .sort(
      (a, b) =>
        b.capsulesDone - a.capsulesDone ||
        b.points - a.points ||
        a.timeMs - b.timeMs ||
        (a.name ?? a.handle ?? a.did).localeCompare(
          b.name ?? b.handle ?? b.did,
        ),
    )

  return {
    mode: ranked.length >= threshold ? 'leaderboard' : 'results',
    finishers: ranked.length,
    threshold,
    plays: playCount,
    capsuleCount,
    maxPoints,
    ranked,
    podium: ranked.slice(0, 3),
    unfinished,
    all: [...ranked, ...unfinished],
    empty: scored.length === 0,
  }
}

/**
 * Points per minute, to one decimal.
 *
 * One decimal because the whole point of the ranking is that a *small* difference
 * decides it: rounded to a whole number, two learners who are clearly apart on the
 * board would print the same rate and the order would look arbitrary. It lives here
 * beside the rule it prints, because three surfaces show it and the fourth decimal
 * place is where they would start to disagree.
 */
export function formatRate(rate: number): string {
  if (!Number.isFinite(rate) || rate <= 0) return '0.0'
  return rate.toFixed(1)
}

/** A duration as `m:ss` — how long a run took, the way a stopwatch says it. */
export function formatTime(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/**
 * One learner's row, wherever they are on the board.
 *
 * A board that only showed the top of itself would leave the learner who played
 * looking for their own name in a list of strangers, so the screen pins their row
 * — and finding it is one lookup rather than a filter over two arrays at the call
 * site, because the caller has to do it in four places and would get it wrong once.
 */
export function findPlayer(
  board: ModuleLeaderboard,
  did: string | undefined,
): ModulePlayerScore | null {
  if (!did) return null
  return board.all.find(player => player.did === did) ?? null
}
